import React, { useState } from 'react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { Checkbox } from '../ui/checkbox'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select'
import { Loader2 } from 'lucide-react'
import ReportEndpoints from '../../endpoints/ReportEndpoints'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'

interface ScheduleExportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  eventId: number
  spotIds: number[]
  locationName: string
  positionName: string
  fromDate: string
  toDate: string
}

const COLUMNS = [
  { key: 'includeShiftSpot', label: 'Shift Spot #', default: true },
  { key: 'includeLocation', label: 'Location', default: true },
  { key: 'includePosition', label: 'Position', default: true },
  { key: 'includeDate', label: 'Date', default: true },
  { key: 'includeStartTime', label: 'Start Time', default: true },
  { key: 'includeEndTime', label: 'End Time', default: true },
  { key: 'includeScheduledHours', label: 'Scheduled Hours', default: true },
  { key: 'includeEmployee', label: 'Employee Name', default: true },
  { key: 'includeEmail', label: 'Email', default: false },
  { key: 'includeMobilePhone', label: 'Mobile Phone', default: false },
  { key: 'includeHomePhone', label: 'Home Phone', default: false },
  { key: 'includeTimeFields', label: 'Punch Clock (In/Out/Break/Net)', default: false },
  { key: 'includeBreakFields', label: 'Break Fields (Manual)', default: false },
  { key: 'includeShiftNotes', label: 'Shift Notes', default: false },
  { key: 'includeSpotNotes', label: 'Spot Notes', default: false },
]

const SORT_OPTIONS = [
  { value: 'date-time', label: 'Date & Time' },
  { value: 'location', label: 'Location' },
  { value: 'position', label: 'Position' },
  { value: 'employee-last-first', label: 'Employee (Last, First)' },
  { value: 'employee-first-last', label: 'Employee (First Last)' },
]

export const ScheduleExportDialog: React.FC<ScheduleExportDialogProps> = ({
  open,
  onOpenChange,
  eventId,
  spotIds,
  locationName,
  positionName,
  fromDate,
  toDate,
}) => {
  const [loading, setLoading] = useState(false)
  const [sortBy, setSortBy] = useState('date-time')
  const [selected, setSelected] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    COLUMNS.forEach(c => { initial[c.key] = c.default })
    return initial
  })

  const toggle = (key: string) => {
    setSelected(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const selectAll = () => {
    const next: Record<string, boolean> = {}
    COLUMNS.forEach(c => { next[c.key] = true })
    setSelected(next)
  }

  const selectNone = () => {
    const next: Record<string, boolean> = {}
    COLUMNS.forEach(c => { next[c.key] = false })
    setSelected(next)
  }

  const handleGenerate = async () => {
    const anySelected = COLUMNS.some(c => selected[c.key])
    if (!anySelected) {
      toast.error('Select at least one column')
      return
    }

    setLoading(true)
    try {
      const params: Record<string, any> = {
        eventId,
        spotIds,
        location: locationName,
        position: positionName,
        fromDate,
        toDate,
        employee: 'All',
        spotStatus: 'Show All',
        rootUrl: location.origin + '/ems/',
        sortBy,
      }
      COLUMNS.forEach(c => {
        params[c.key] = selected[c.key] ? 'true' : 'false'
      })

      const res = await ReportEndpoints.getExtendedScheduleReport(params)
      const w = window.open()
      if (w) {
        w.document.write(res.data)
        w.document.close()
      }
      onOpenChange(false)
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to generate report'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Export Schedule</DialogTitle>
          <DialogDescription>
            Choose which columns to include and how to sort the report.
            {spotIds.length > 0 && (
              <span className="block mt-1 text-xs">{spotIds.length} shift spot(s) selected</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Columns</Label>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={selectAll}>All</Button>
                <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={selectNone}>None</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {COLUMNS.map(col => (
                <div key={col.key} className="flex items-center gap-2">
                  <Checkbox
                    id={col.key}
                    checked={!!selected[col.key]}
                    onCheckedChange={() => toggle(col.key)}
                  />
                  <Label htmlFor={col.key} className="text-sm font-normal cursor-pointer">{col.label}</Label>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Sort By</Label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleGenerate} disabled={loading}>
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating...</> : 'Generate Report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
